import React, { useEffect, useId, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { confirmFinancialPrimaryPayer, FinancialPrimaryPayerTargetChangedError, previewFinancialPrimaryPayer, type FinancialPrimaryPayerPreview } from '../../services/FinancialPayerPrimaryService';
import { FinancialPrimaryPayerConflictError } from '../../services/financialPayerPrimaryConflict';

function dateLabel(date: string): string {
  return date.split('-').reverse().join('/');
}

export default function FinancialPrimaryPayerCell({ clientId, linkId, patientName, payerName, primary, disabled, onChanged }: {
  clientId: string; linkId: string; patientName: string; payerName: string;
  primary: boolean; disabled: boolean; onChanged: () => void;
}) {
  const titleId = useId();
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState<FinancialPrimaryPayerPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const requestId = useRef(0);
  const checkboxRef = useRef<HTMLInputElement>(null);
  const restoreFocus = useRef(false);

  useEffect(() => () => { requestId.current += 1; }, []);
  useEffect(() => {
    if (!open && restoreFocus.current) {
      checkboxRef.current?.focus();
      restoreFocus.current = false;
    }
  }, [open]);

  function close() {
    requestId.current += 1;
    restoreFocus.current = true;
    setOpen(false);
  }

  async function begin() {
    if (primary || disabled || open) return;
    const request = ++requestId.current;
    setOpen(true); setLoading(true); setPreview(null); setError(''); setNotice('');
    try {
      const current = await previewFinancialPrimaryPayer(clientId, linkId);
      if (request !== requestId.current) return;
      setPreview(current);
    } catch (cause) {
      if (request === requestId.current) setError(cause instanceof Error ? cause.message : 'Não foi possível consultar o pagador principal.');
    } finally {
      if (request === requestId.current) setLoading(false);
    }
  }

  async function confirm() {
    if (!preview || saving || loading || preview.isPrimary) return;
    setSaving(true); setError(''); setNotice('');
    try {
      await confirmFinancialPrimaryPayer(clientId, linkId, preview.conflicts.map(link => link.id), preview.targetVersion);
      setOpen(false);
      toast.success('Pagador principal atualizado.');
      onChanged();
    } catch (cause) {
      if (cause instanceof FinancialPrimaryPayerTargetChangedError) {
        setPreview(null); setLoading(true);
        try {
          setPreview(await previewFinancialPrimaryPayer(clientId, linkId));
          setNotice('Os dados do pagador selecionado mudaram. Confira os dados atualizados abaixo e confirme novamente.');
        } catch (refreshError) {
          setError(refreshError instanceof Error ? refreshError.message : 'Não foi possível conferir o pagador atualizado.');
        } finally {
          setLoading(false);
        }
      } else if (cause instanceof FinancialPrimaryPayerConflictError) {
        setPreview({ ...preview, conflicts: cause.conflicts });
        setNotice('O pagador principal mudou enquanto você conferia. Revise os nomes abaixo e confirme novamente.');
      } else {
        setError(cause instanceof Error ? cause.message : 'Não foi possível alterar o pagador principal.');
      }
    } finally {
      setSaving(false);
    }
  }

  const title = primary ? 'Pagador principal atual' : disabled ? 'Pagador indisponível para alteração' : 'Definir como pagador principal';
  return <React.Fragment>
    <input ref={checkboxRef} type="checkbox" checked={primary} disabled={disabled || primary || open}
      onChange={() => void begin()} title={title}
      aria-label={`${title}: ${payerName}, paciente ${patientName}`}
      className="h-4 w-4 cursor-pointer rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:cursor-default" />
    {open && <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby={titleId}
      onKeyDown={event => {
        if (event.key === 'Escape' && !saving) { event.stopPropagation(); close(); }
        if (event.key === 'Tab') {
          const buttons = event.currentTarget.querySelectorAll<HTMLButtonElement>('button:not(:disabled)');
          const first = buttons[0]; const last = buttons[buttons.length - 1];
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
        }
      }}>
      <div className="max-h-[90vh] w-full max-w-lg space-y-4 overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
        <h3 id={titleId} className="text-lg font-semibold">Alterar pagador principal?</h3>
        {loading && <p role="status" className="text-sm text-gray-600">Conferindo pagadores…</p>}
        {notice && <p role="status" className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">{notice}</p>}
        {preview && (preview.isPrimary ? <p className="text-sm text-gray-700"><strong>{preview.payerName}</strong> já é o pagador principal deste paciente. Atualize a lista para conferir.</p> : <>
          <p className="text-sm text-gray-700">Deseja definir <strong>{preview.payerName}</strong> como pagador principal de <strong>{preview.patientName}</strong>?</p>
          <p className="text-sm text-gray-600">Vigência: {dateLabel(preview.effectiveFrom)} até {preview.effectiveUntil ? dateLabel(preview.effectiveUntil) : 'sem data final'}.</p>
          {preview.conflicts.length > 0 && <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <p>Os seguintes pagadores deixarão de ser principais:</p>
            <ul className="mt-2 space-y-1">{preview.conflicts.map(link => <li key={link.id}><strong>{link.payerName}</strong> — {dateLabel(link.effectiveFrom)} até {link.effectiveUntil ? dateLabel(link.effectiveUntil) : 'sem data final'}</li>)}</ul>
          </div>}
          <p className="text-sm text-gray-600">Os cadastros dos pagadores, os contratos e as datas de vigência serão mantidos.</p>
        </>)}
        {error && <p role="alert" className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</p>}
        <div className="flex justify-end gap-3 border-t pt-4">
          <button autoFocus type="button" disabled={saving} onClick={close} className="rounded-md border px-4 py-2 disabled:opacity-50">Cancelar</button>
          {preview?.isPrimary ? <button type="button" onClick={onChanged} className="rounded-md bg-primary-600 px-4 py-2 font-medium text-white">Atualizar lista</button>
            : <button type="button" disabled={loading || saving || !preview} onClick={() => void confirm()} className="rounded-md bg-primary-600 px-4 py-2 font-medium text-white disabled:opacity-50">{saving ? 'Salvando…' : 'Sim, alterar principal'}</button>}
        </div>
      </div>
    </div>}
  </React.Fragment>;
}
